import React from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyResultsQuestion } from '@/pages/Mail/components/SurveyResultsQuestion';
import type { SurveyResults } from '@/types/survey';

const RING_SIZE = 50;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface Props {
  results: SurveyResults;
}

export const SurveyResultsView: React.FC<Props> = ({ results }) => {
  const { t } = useTranslation();

  const {
    respondeesCount,
    finishedRespondeesCount,
    finishedPercentage,
    questionCount,
    questions,
  } = results;
  const ringOffset = RING_CIRCUMFERENCE * (1 - finishedPercentage / 100);

  return (
    <div className="flex flex-col gap-2 pb-4 md:gap-3 md:pb-0">
      <p className="text-subhead-14 text-subtle-foreground">
        {t('mail.surveys.detail.results')}
      </p>

      <div className="flex items-center gap-6 rounded-xl bg-card p-4 shadow-card md:gap-5">
        <div className="relative flex shrink-0 items-center justify-center">
          <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              strokeWidth={RING_STROKE}
              className="stroke-on-primary-surface"
            />
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={ringOffset}
              className="stroke-primary"
            />
          </svg>
          <span className="text-caption-10 absolute font-semibold text-primary">
            {finishedPercentage}%
          </span>
        </div>

        <div className="flex gap-6 md:gap-8">
          <div className="flex flex-col gap-0.5">
            <p className="text-body-13 text-subtle-foreground">
              {t('mail.surveys.detail.respondees')}
            </p>
            <p className="text-callout-16 text-primary">
              {finishedRespondeesCount}/{respondeesCount}
            </p>
          </div>

          <div className="flex flex-col gap-0.5">
            <p className="text-body-13 text-subtle-foreground">
              {t('mail.surveys.detail.questionCount')}
            </p>
            <p className="text-callout-16 text-primary">{questionCount}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 pt-2 md:pt-0">
        {questions.map((question, position) => (
          <SurveyResultsQuestion
            key={position}
            index={position + 1}
            question={question}
          />
        ))}
      </div>
    </div>
  );
};
